import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { IdealAssessment, EvaluationCriteria, ApproachCategory } from '../../types';
import { LLM_VALUE_CORRECTNESS_VALUES, LLM_UNIT_CORRECTNESS_VALUES } from '@padcanvas/shared';
import type { LlmValueCorrectness, LlmUnitCorrectness } from '@padcanvas/shared';
import { LatexText } from '@shared-ui/latex';
import { ImageViewer, BackButton } from '@shared-ui/components';

export default function LabelingInterface() {
  const { datasetId, itemId } = useParams<{ datasetId: string; itemId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State for the label form
  const [correctnessValue, setCorrectnessValue] = useState<LlmValueCorrectness>('correct');
  const [correctnessUnit, setCorrectnessUnit] = useState<LlmUnitCorrectness>('not_needed');
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [correctSolution, setCorrectSolution] = useState('');
  const [isComplete, setIsComplete] = useState(true);
  const [isCorrect, setIsCorrect] = useState(true);
  const [isCalculationMistake, setIsCalculationMistake] = useState(false);
  const [optimalScore, setOptimalScore] = useState(3);
  const [requiredPoints, setRequiredPoints] = useState<string[]>([]);
  const [prohibitedPoints, setProhibitedPoints] = useState<string[]>([]);
  const [confidence, setConfidence] = useState<'high' | 'medium' | 'low'>('medium');
  const [notes, setNotes] = useState('');
  const [newRequiredPoint, setNewRequiredPoint] = useState('');
  const [newProhibitedPoint, setNewProhibitedPoint] = useState('');

  // New: LLM assessment ground truth
  const [expectedStudentAnswer, setExpectedStudentAnswer] = useState('');
  const [expectedOverallAssessment, setExpectedOverallAssessment] = useState<'correct' | 'correct_but_inefficient' | 'partially_correct' | 'incorrect'>('correct');
  const [expectedApproachCategory, setExpectedApproachCategory] = useState<ApproachCategory>('optimal');
  const [expectedStepCount, setExpectedStepCount] = useState<number | ''>('');
  const [expectedErrorFraction, setExpectedErrorFraction] = useState(0);

  // New: Computed score ground truth
  const [expectedFinalScore, setExpectedFinalScore] = useState<number | ''>(5);
  const [expectedCorrectnessScore, setExpectedCorrectnessScore] = useState<number | ''>('');
  const [expectedApproachScore, setExpectedApproachScore] = useState<number | ''>('');
  const [expectedSpeedScore, setExpectedSpeedScore] = useState<number | ''>('');
  const [showScoreOverrides, setShowScoreOverrides] = useState(false);

  // Get dataset info
  const { data: dataset } = useQuery({
    queryKey: ['evalDataset', datasetId],
    queryFn: () => adminApi.getEvalDataset(datasetId!),
    enabled: !!datasetId,
  });

  // Get all items list (for navigation — need all items, not just pending)
  const { data: itemsData } = useQuery({
    queryKey: ['datasetItems', datasetId, 'all'],
    queryFn: () => adminApi.getDatasetItems(datasetId!, { limit: 200 }),
    enabled: !!datasetId,
  });

  // Get specific item if itemId provided
  const { data: currentItem, isLoading } = useQuery({
    queryKey: ['datasetItem', datasetId, itemId],
    queryFn: () => adminApi.getDatasetItem(datasetId!, itemId!),
    enabled: !!datasetId && !!itemId,
  });

  // If no itemId, redirect to first pending item
  useEffect(() => {
    if (!itemId && itemsData?.items && itemsData.items.length > 0) {
      const firstPending = itemsData.items.find(i => i.status === 'pending');
      if (firstPending) {
        navigate(`/eval/datasets/${datasetId}/items/${firstPending.id}`, { replace: true });
      }
    }
  }, [itemId, itemsData, datasetId, navigate]);

  // Load existing label data if editing
  useEffect(() => {
    if (currentItem?.labelData) {
      // Item is already labeled - we'd need to fetch the labeled output to get idealAssessment
      // For now, just load what we have from labelData
      setRequiredPoints(currentItem.labelData.evaluationCriteria.requiredFeedbackPoints || []);
      setProhibitedPoints(currentItem.labelData.evaluationCriteria.prohibitedPoints || []);
      setConfidence(currentItem.labelData.confidence);
      setNotes(currentItem.labelData.notes || '');
    } else if (currentItem?.input) {
      // Pre-fill correct answer from question if available
      setCorrectAnswer(currentItem.input.question?.correctAnswer || '');
    }
  }, [currentItem]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const idealAssessment: IdealAssessment = {
        correctness: {
          value: correctnessValue,
          unit: correctnessUnit,
        },
        correctLlmAnswer: correctAnswer,
        correctLlmSolution: correctSolution || undefined,
        approachQuality: {
          isComplete,
          isCorrect,
          isCalculationMistake,
          optimalScore,
        },
        // New LLM assessment ground truth
        expectedStudentAnswer: expectedStudentAnswer || undefined,
        expectedOverallAssessment,
        expectedApproachCategory,
        expectedStepCount: expectedStepCount !== '' ? { min: expectedStepCount, max: expectedStepCount } : undefined,
        expectedErrorFraction,
        // New computed score ground truth — stored as exact ranges (min === max).
        expectedFinalScore: expectedFinalScore !== '' ? { min: expectedFinalScore, max: expectedFinalScore } : undefined,
        expectedCorrectnessScore: expectedCorrectnessScore !== '' ? { min: expectedCorrectnessScore, max: expectedCorrectnessScore } : undefined,
        expectedApproachScore: expectedApproachScore !== '' ? { min: expectedApproachScore, max: expectedApproachScore } : undefined,
        expectedSpeedScore: expectedSpeedScore !== '' ? { min: expectedSpeedScore, max: expectedSpeedScore } : undefined,
      };

      const evaluationCriteria: EvaluationCriteria = {
        requiredFeedbackPoints: requiredPoints.length > 0 ? requiredPoints : undefined,
        prohibitedPoints: prohibitedPoints.length > 0 ? prohibitedPoints : undefined,
      };

      return adminApi.labelDatasetItem(datasetId!, itemId!, {
        idealAssessment,
        evaluationCriteria,
        labeledBy: 'admin', // TODO: Get actual user
        confidence,
        notes: notes || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datasetItems', datasetId] });
      queryClient.invalidateQueries({ queryKey: ['datasetItem', datasetId, itemId] });
      queryClient.invalidateQueries({ queryKey: ['evalDataset', datasetId] });
      // Navigate to next pending item
      goToNext();
    },
  });

  const goToNext = () => {
    if (!itemsData?.items) return;
    const currentIndex = itemsData.items.findIndex(i => i.id === itemId);
    // Find next item — prefer next pending, fallback to next by index
    const nextPending = itemsData.items.find((i, idx) => idx > currentIndex && i.id !== itemId && i.status === 'pending');
    const nextByIndex = currentIndex < itemsData.items.length - 1 ? itemsData.items[currentIndex + 1] : null;
    const next = nextPending || nextByIndex;
    if (next) {
      navigate(`/eval/datasets/${datasetId}/items/${next.id}`);
    } else {
      navigate(`/eval/datasets/${datasetId}`);
    }
  };

  const goToPrevious = () => {
    if (!itemsData?.items) return;
    const currentIndex = itemsData.items.findIndex(i => i.id === itemId);
    if (currentIndex > 0) {
      navigate(`/eval/datasets/${datasetId}/items/${itemsData.items[currentIndex - 1].id}`);
    }
  };

  const addRequiredPoint = () => {
    if (newRequiredPoint.trim()) {
      setRequiredPoints([...requiredPoints, newRequiredPoint.trim()]);
      setNewRequiredPoint('');
    }
  };

  const addProhibitedPoint = () => {
    if (newProhibitedPoint.trim()) {
      setProhibitedPoints([...prohibitedPoints, newProhibitedPoint.trim()]);
      setNewProhibitedPoint('');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!currentItem || !currentItem.input) {
    return (
      <div className="empty-state">
        <h3>No Item Selected</h3>
        <p>No pending items remaining in this dataset.</p>
        <Link to={`/eval/datasets/${datasetId}`} className="btn btn-primary">
          Back to Dataset
        </Link>
      </div>
    );
  }

  const input = currentItem.input;
  const pendingCount = itemsData?.pendingCount || 0;
  const currentIndex = itemsData?.items?.findIndex(i => i.id === itemId) ?? 0;
  const totalItems = itemsData?.items?.length || 0;
  const isLabeled = currentItem.status === 'labeled';

  return (
    <div className="labeling-interface">
      <header className="labeling-header">
        <div className="header-left">
          <BackButton to={`/eval/datasets/${datasetId}`} />
          <h1>Labeling: {dataset?.name || 'Dataset'}</h1>
        </div>
        <div className="header-right">
          <span className="progress-text">{currentIndex + 1} of {totalItems} ({pendingCount} pending)</span>
          <div className="nav-buttons">
            <button className="btn btn-secondary btn-sm" onClick={goToPrevious} disabled={currentIndex === 0}>
              &larr; Prev
            </button>
            <button className="btn btn-secondary btn-sm" onClick={goToNext}>
              Skip &rarr;
            </button>
          </div>
        </div>
      </header>

      <div className="labeling-content">
        <div className="input-panel">
          <section className="panel-section">
            <h3>Question</h3>
            <div className="question-meta">
              <span className="badge">{input.question?.subject}</span>
              <span className={`badge difficulty-${input.question?.difficulty}`}>
                {input.question?.difficulty}
              </span>
              <span className="badge">{input.question?.questionType}</span>
            </div>
            <div className="question-text"><LatexText text={input.question?.text ?? ''} /></div>
            {input.question?.imageUrl && (
              <ImageViewer src={input.question?.imageUrl} alt="Question" className="question-image" />
            )}
            {input.question?.correctAnswer && (
              <div className="expected-answer">
                <strong>Expected Answer:</strong> <LatexText text={input.question?.correctAnswer} />
              </div>
            )}
          </section>

          <section className="panel-section">
            <h3>Student Profile</h3>
            <div className="profile-info">
              <span>Class: {input.studentProfile?.class}</span>
              <span>Proficiency: {input.studentProfile?.proficiency}</span>
              {input.studentProfile?.targetExam && (
                <span>Target Exam: {input.studentProfile?.targetExam}</span>
              )}
            </div>
          </section>

          <section className="panel-section">
            <h3>Student Solution</h3>
            {input.studentSolution?.textAnswer && (
              <div className="solution-text">
                <strong>Text Answer:</strong>
                <pre>{input.studentSolution?.textAnswer}</pre>
              </div>
            )}
            {input.studentSolution?.imageUrl && (
              <div className="solution-image">
                <strong>Canvas Image:</strong>
                <ImageViewer src={input.studentSolution?.imageUrl} alt="Student solution" />
              </div>
            )}
            {input.studentSolution?.selectedOption && (
              <div className="solution-mcq">
                <strong>Selected Option:</strong> {input.studentSolution?.selectedOption}
              </div>
            )}
            {input.studentSolution?.timeTakenSeconds && (
              <div className="time-taken">
                Time taken: {Math.floor(input.studentSolution?.timeTakenSeconds / 60)}m {input.studentSolution?.timeTakenSeconds % 60}s
              </div>
            )}
          </section>
        </div>

        <div className="label-panel">
          <section className="panel-section">
            <h3>Correctness</h3>
            <div className="form-row">
              <label>Answer Value</label>
              <div className="radio-group">
                {LLM_VALUE_CORRECTNESS_VALUES.map(v => (
                  <label key={v} className="radio-label">
                    <input
                      type="radio"
                      name="correctnessValue"
                      value={v}
                      checked={correctnessValue === v}
                      onChange={() => setCorrectnessValue(v)}
                    />
                    {v.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-row">
              <label>Unit</label>
              <div className="radio-group">
                {LLM_UNIT_CORRECTNESS_VALUES.map(v => (
                  <label key={v} className="radio-label">
                    <input
                      type="radio"
                      name="correctnessUnit"
                      value={v}
                      checked={correctnessUnit === v}
                      onChange={() => setCorrectnessUnit(v)}
                    />
                    {v.replace('_', ' ')}
                  </label>
                ))}
              </div>
            </div>
          </section>

          <section className="panel-section">
            <h3>Answers</h3>
            <div className="form-row">
              <label>Expected Student Answer</label>
              <input
                type="text"
                value={expectedStudentAnswer}
                onChange={(e) => setExpectedStudentAnswer(e.target.value)}
                placeholder="What the student actually wrote (e.g., 42, option B)"
              />
              <span className="field-hint">What the LLM should extract as studentLlmAnswer</span>
            </div>
            <div className="form-row">
              <label>Correct Answer</label>
              <input
                type="text"
                value={correctAnswer}
                onChange={(e) => setCorrectAnswer(e.target.value)}
                placeholder="e.g., 42 m/s"
              />
            </div>
            <div className="form-row">
              <label>Correct Solution (optional)</label>
              <textarea
                value={correctSolution}
                onChange={(e) => setCorrectSolution(e.target.value)}
                placeholder="Step-by-step solution..."
                rows={4}
              />
            </div>
          </section>

          <section className="panel-section">
            <h3>Overall & Approach</h3>
            <div className="form-row">
              <label>Overall Assessment</label>
              <select
                value={expectedOverallAssessment}
                onChange={(e) => setExpectedOverallAssessment(e.target.value as typeof expectedOverallAssessment)}
                className="form-select"
              >
                <option value="correct">Correct</option>
                <option value="correct_but_inefficient">Correct but Inefficient</option>
                <option value="partially_correct">Partially Correct</option>
                <option value="incorrect">Incorrect</option>
              </select>
            </div>
            <div className="form-row">
              <label>Approach Category</label>
              <select
                value={expectedApproachCategory}
                onChange={(e) => setExpectedApproachCategory(e.target.value as ApproachCategory)}
                className="form-select"
              >
                <option value="optimal">Optimal</option>
                <option value="suboptimal">Suboptimal</option>
                <option value="calculation_error">Calculation Error</option>
                <option value="partially_correct">Partially Correct</option>
                <option value="incomplete">Incomplete</option>
                <option value="wrong_approach">Wrong Approach</option>
                <option value="missing">Missing (no working shown)</option>
              </select>
            </div>
          </section>

          <section className="panel-section">
            <h3>Steps</h3>
            <div className="form-row">
              <label>Expected Step Count</label>
              <input
                type="number"
                min="0"
                max="20"
                value={expectedStepCount}
                onChange={(e) => setExpectedStepCount(e.target.value ? parseInt(e.target.value) : '')}
                placeholder="e.g., 4"
              />
              <span className="field-hint">How many steps should the LLM identify? (±1 tolerance)</span>
            </div>
            <div className="form-row">
              <label>Expected Error Fraction: {(expectedErrorFraction * 100).toFixed(0)}%</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={expectedErrorFraction}
                onChange={(e) => setExpectedErrorFraction(parseFloat(e.target.value))}
              />
              <span className="field-hint">0% = all steps correct, 100% = all steps wrong</span>
            </div>
          </section>

          <section className="panel-section">
            <h3>Approach Quality (Legacy)</h3>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isComplete}
                  onChange={(e) => setIsComplete(e.target.checked)}
                />
                Steps are complete
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isCorrect}
                  onChange={(e) => setIsCorrect(e.target.checked)}
                />
                Steps are correct
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isCalculationMistake}
                  onChange={(e) => setIsCalculationMistake(e.target.checked)}
                />
                Has calculation mistake
              </label>
            </div>
            <div className="form-row">
              <label>Optimal Score (0-5): {optimalScore}</label>
              <input
                type="range"
                min="0"
                max="5"
                value={optimalScore}
                onChange={(e) => setOptimalScore(parseInt(e.target.value))}
              />
              <span className="range-hint">5 is most optimal</span>
            </div>
          </section>

          <section className="panel-section">
            <h3>Required Feedback Points</h3>
            <div className="points-list">
              {requiredPoints.map((point, i) => (
                <div key={i} className="point-item">
                  <span>{point}</span>
                  <button
                    className="remove-btn"
                    onClick={() => setRequiredPoints(requiredPoints.filter((_, idx) => idx !== i))}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <div className="add-point">
              <input
                type="text"
                value={newRequiredPoint}
                onChange={(e) => setNewRequiredPoint(e.target.value)}
                placeholder="Add required point..."
                onKeyDown={(e) => e.key === 'Enter' && addRequiredPoint()}
              />
              <button className="btn btn-secondary btn-sm" onClick={addRequiredPoint}>Add</button>
            </div>
          </section>

          <section className="panel-section">
            <h3>Prohibited Points</h3>
            <div className="points-list">
              {prohibitedPoints.map((point, i) => (
                <div key={i} className="point-item prohibited">
                  <span>{point}</span>
                  <button
                    className="remove-btn"
                    onClick={() => setProhibitedPoints(prohibitedPoints.filter((_, idx) => idx !== i))}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <div className="add-point">
              <input
                type="text"
                value={newProhibitedPoint}
                onChange={(e) => setNewProhibitedPoint(e.target.value)}
                placeholder="Add prohibited point..."
                onKeyDown={(e) => e.key === 'Enter' && addProhibitedPoint()}
              />
              <button className="btn btn-secondary btn-sm" onClick={addProhibitedPoint}>Add</button>
            </div>
          </section>

          <section className="panel-section">
            <h3>Expected Scores</h3>
            <div className="form-row">
              <label>Expected Final Score (0-10): {expectedFinalScore !== '' ? expectedFinalScore : '—'}</label>
              <input
                type="range"
                min="0"
                max="10"
                value={expectedFinalScore !== '' ? expectedFinalScore : 5}
                onChange={(e) => setExpectedFinalScore(parseInt(e.target.value))}
              />
              <span className="field-hint">Required. What score should the student get overall?</span>
            </div>
            <div className="form-row">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowScoreOverrides(!showScoreOverrides)}
              >
                {showScoreOverrides ? 'Hide' : 'Show'} Component Score Overrides
              </button>
            </div>
            {showScoreOverrides && (
              <div className="score-overrides">
                <div className="form-row">
                  <label>Expected Correctness Score (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={expectedCorrectnessScore}
                    onChange={(e) => setExpectedCorrectnessScore(e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-row">
                  <label>Expected Approach Score (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={expectedApproachScore}
                    onChange={(e) => setExpectedApproachScore(e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-row">
                  <label>Expected Speed Score (0-10)</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={expectedSpeedScore}
                    onChange={(e) => setExpectedSpeedScore(e.target.value ? parseInt(e.target.value) : '')}
                    placeholder="Optional"
                  />
                </div>
              </div>
            )}
          </section>

          <section className="panel-section">
            <h3>Confidence & Notes</h3>
            <div className="form-row">
              <label>Confidence</label>
              <div className="radio-group">
                {(['high', 'medium', 'low'] as const).map(v => (
                  <label key={v} className="radio-label">
                    <input
                      type="radio"
                      name="confidence"
                      value={v}
                      checked={confidence === v}
                      onChange={() => setConfidence(v)}
                    />
                    {v}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-row">
              <label>Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </section>

          {saveMutation.error && (
            <div className="error-message">
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save label'}
            </div>
          )}

          <div className="save-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !correctAnswer}
            >
              {saveMutation.isPending ? 'Saving...' : (isLabeled ? 'Update Label' : 'Save & Next')}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .labeling-interface {
          height: 100vh;
          display: flex;
          flex-direction: column;
          color: var(--text-primary);
        }

        .labeling-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          background: var(--bg-primary);
          border-bottom: 1px solid var(--border-color);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-left h1 {
          margin: 0;
          font-size: 18px;
          color: var(--text-primary);
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .progress-text {
          color: var(--text-secondary);
          font-size: 14px;
        }

        .nav-buttons {
          display: flex;
          gap: 8px;
        }

        .labeling-content {
          flex: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          overflow: hidden;
        }

        .input-panel, .label-panel {
          padding: 24px;
          overflow-y: auto;
        }

        .input-panel {
          background: var(--bg-secondary);
          border-right: 1px solid var(--border-color);
        }

        .label-panel {
          background: var(--bg-primary);
        }

        .panel-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border-color);
        }

        .panel-section:last-child {
          border-bottom: none;
        }

        .panel-section h3 {
          margin: 0 0 12px;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .question-meta {
          display: flex;
          gap: 8px;
          margin-bottom: 12px;
        }

        .badge {
          padding: 4px 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
          font-size: 12px;
          text-transform: capitalize;
          color: var(--text-secondary);
        }

        .difficulty-easy { background: var(--color-success-bg); color: var(--status-success-text, #065f46); }
        .difficulty-medium { background: var(--color-warning-bg, #fef3c7); color: var(--status-warning-text, #92400e); }
        .difficulty-hard { background: var(--color-error-bg); color: var(--status-error-text, #991b1b); }

        .question-text {
          font-size: 16px;
          line-height: 1.6;
          margin: 0 0 12px;
          color: var(--text-primary);
        }

        .question-image, .solution-image img {
          max-width: 100%;
          display: block;
          border-radius: 8px;
          margin-top: 12px;
          border: 1px solid var(--border-color);
        }

        .expected-answer {
          padding: 12px;
          background: var(--color-success-bg, #ecfdf5);
          border-radius: 6px;
          font-size: 14px;
          margin-top: 12px;
          color: var(--text-primary);
        }

        .profile-info {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-size: 14px;
          color: var(--text-primary);
        }

        .solution-text pre {
          white-space: pre-wrap;
          font-family: inherit;
          margin: 8px 0 0;
          padding: 12px;
          background: var(--bg-tertiary);
          border-radius: 6px;
          color: var(--text-primary);
        }

        .form-row {
          margin-bottom: 16px;
        }

        .form-row label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .form-row input[type="text"],
        .form-row textarea {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border-color-dark);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .form-row input:focus,
        .form-row textarea:focus {
          outline: none;
          border-color: var(--color-accent);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .radio-group {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }

        .radio-label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          cursor: pointer;
          text-transform: capitalize;
          color: var(--text-primary);
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 16px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          cursor: pointer;
          color: var(--text-primary);
        }

        .range-hint, .field-hint {
          display: block;
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 4px;
        }

        .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border-color-dark);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .form-select:focus {
          outline: none;
          border-color: var(--color-accent);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
        }

        .form-row input[type="number"] {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border-color-dark);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .score-overrides {
          padding: 12px;
          background: var(--bg-secondary);
          border-radius: 6px;
          margin-top: 8px;
        }

        .points-list {
          margin-bottom: 12px;
        }

        .point-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 12px;
          background: var(--color-success-bg, #ecfdf5);
          border-radius: 6px;
          margin-bottom: 6px;
          font-size: 14px;
          color: var(--text-primary);
        }

        .point-item.prohibited {
          background: var(--color-error-bg, #fef2f2);
        }

        .remove-btn {
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: var(--text-tertiary);
        }

        .remove-btn:hover {
          color: var(--color-error);
        }

        .add-point {
          display: flex;
          gap: 8px;
        }

        .add-point input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid var(--border-color-dark);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .error-message {
          padding: 12px;
          background: var(--color-error-bg, #fef2f2);
          border: 1px solid var(--color-error, #fecaca);
          border-radius: 6px;
          color: var(--color-error);
          font-size: 14px;
          margin-bottom: 16px;
        }

        .save-actions {
          position: sticky;
          bottom: 0;
          padding: 16px 0;
          background: var(--bg-primary);
          border-top: 1px solid var(--border-color);
        }

        .btn {
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
        }

        .btn-primary {
          background: var(--color-accent);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary);
        }

        .btn-primary:disabled {
          background: var(--color-accent);
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: var(--bg-tertiary);
          color: var(--text-primary);
          border: 1px solid var(--border-color-dark);
        }

        .btn-lg {
          width: 100%;
          padding: 12px 24px;
          font-size: 16px;
        }

        .btn-sm {
          padding: 6px 12px;
          font-size: 12px;
        }

        .loading-container, .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          gap: 16px;
          color: var(--text-primary);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border-color);
          border-top-color: var(--color-accent);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
